import * as React from "react"
import { RefObject } from "react"

type PlaidContextType = {
  setSignUpPhoneNumber: (newValue: string | null) => void
  signUpPhoneNumber: string | null
  linkToken: {
    linkToken: string | null
    tokenId: string | null
  }
  fallbackLinkToken: {
    linkToken: string | null
    tokenId: string | null
  }
  setLinkToken: (newValue: { linkToken: string | null; tokenId: string | null }) => void
  setFallbackLinkToken: (newValue: { linkToken: string | null; tokenId: string | null }) => void
  openLayer: RefObject<() => void>
  isPlaidLoading: boolean
  setIsPlaidLoading: (newVal: boolean) => void
  openPlaid: RefObject<() => void>
  isLayerEligible: boolean
  /**
   * Create/submit the Layer session for the current phone + link token.
   * Call from the bank step only — never while the user is still on OTP.
   */
  startLayerSession: () => void
  restart: () => void
  /**
   * When true, the emitter opens Layer as soon as LAYER_READY fires for the
   * current session. Flipped to true by `usePlaidLayer` after `submit()`
   * completes for the fresh session (see `pendingOpenAfterSubmit`).
   */
  openInOnReady: RefObject<boolean>
  /**
   * Set by Layer `restart()` to signal "the user tapped Connect bank; open
   * Layer as soon as the fresh session is submitted-and-ready". Consumed
   * inside `usePlaidLayer.startLayer` after `submit()` completes.
   * (ExternalAuth restart re-opens `fallbackLinkToken` via `openPlaid` and
   * does not arm this flag.)
   *
   * We deliberately don't set `openInOnReady` synchronously in `restart()`,
   * because a stale LAYER_READY from a previous native handler could arrive
   * before the new session even calls its `onLoad`, and would open Link
   * against a handler whose phone hasn't been submitted yet — the exact
   * "Please submit Phone Number before opening Link" bug.
   */
  pendingOpenAfterSubmit: RefObject<boolean>
  /** True while a Layer session is being created but LAYER_READY / LAYER_NOT_AVAILABLE / EXIT haven't resolved it yet. */
  isLayerSessionLoading: boolean
  setIsLayerSessionLoading: (value: boolean) => void
  appendEvent: (event: string) => void
  setStartFallbackFlow: (arg: () => Promise<unknown>) => void
  startFallbackFlow: RefObject<() => Promise<unknown>>
}

export const PlaidContext = React.createContext<PlaidContextType>({
  signUpPhoneNumber: null,
  setSignUpPhoneNumber: () => null,
  linkToken: {
    linkToken: null,
    tokenId: null,
  },
  fallbackLinkToken: {
    linkToken: null,
    tokenId: null,
  },
  setLinkToken: () => null,
  setFallbackLinkToken: () => null,
  openLayer: { current: () => null },
  isPlaidLoading: true,
  setIsPlaidLoading: (newVal: boolean) => null,
  openPlaid: { current: () => null },
  isLayerEligible: false,
  startLayerSession: () => null,
  restart: () => null,
  openInOnReady: { current: false },
  pendingOpenAfterSubmit: { current: false },
  isLayerSessionLoading: false,
  setIsLayerSessionLoading: () => null,
  appendEvent: () => null,
  setStartFallbackFlow: () => null,
  startFallbackFlow: {
    current: () => Promise.resolve(),
  },
})
